import React, { useState, useEffect, useCallback } from "react";
import { Search, Download, FileText, User, Mail, Loader2, Check } from "lucide-react";
import apiClient from "@/api/client";

const MUTUELLE_OPTIONS = [
  "Partena Mut",
  "Mutualite Libre Mutplus",
  "Mutualia",
  "Mutualite Neutre",
  "Solidaris",
];

const MutuellePage = () => {
  const [search, setSearch] = useState("");
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMutuelle, setSelectedMutuelle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // {type: "success"|"error", message: string}

  const searchMembers = useCallback(async (query) => {
    if (!query || query.length < 2) { setMembers([]); return; }
    setLoading(true);
    try {
      const res = await apiClient.get(`/members?search=${encodeURIComponent(query)}`);
      setMembers(res.data.slice(0, 20));
    } catch { setMembers([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchMembers(search), 300);
    return () => clearTimeout(timer);
  }, [search, searchMembers]);

  // Reset email status when member or mutuelle changes
  useEffect(() => { setEmailStatus(null); }, [selectedMember, selectedMutuelle]);

  const handleGenerate = async () => {
    if (!selectedMember || !selectedMutuelle) return;
    setGenerating(true);
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiUrl}/api/mutuelle/generate-for-member`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ member_id: selectedMember.id, mutuelle: selectedMutuelle }),
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || "Erreur lors de la génération"); }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Mutuelle_${selectedMutuelle.replace(/ /g, "_")}_${selectedMember.first_name}_${selectedMember.last_name}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { alert(err.message); }
    finally { setGenerating(false); }
  };

  const handleSendEmail = async () => {
    if (!selectedMember || !selectedMutuelle) return;
    if (!selectedMember.email) { setEmailStatus({ type: "error", message: "Ce membre n'a pas d'adresse email" }); return; }
    setSending(true); setEmailStatus(null);
    try {
      const res = await apiClient.post("/mutuelle/send-email", { member_id: selectedMember.id, mutuelle: selectedMutuelle });
      setEmailStatus({ type: "success", message: `Email envoyé à ${res.data.email}` });
    } catch (err) {
      setEmailStatus({ type: "error", message: err.response?.data?.detail || "Erreur lors de l'envoi de l'email" });
    } finally { setSending(false); }
  };

  const canAct = selectedMember && selectedMutuelle;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto" data-testid="mutuelle-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#162d3d] flex items-center gap-3">
          <FileText className="text-[#3899ec]" size={28} />
          Fiche Mutuelle
        </h1>
        <p className="text-sm text-[#577083] mt-1">
          Générer un formulaire mutuelle pré-rempli pour un membre existant
        </p>
      </div>

      {/* Step 1: Search member */}
      <div className="bg-white rounded-xl border border-[#dfe5eb] p-6 mb-6 shadow-sm" data-testid="member-search-section">
        <h2 className="text-sm font-semibold text-[#162d3d] mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#3899ec] text-white flex items-center justify-center text-xs font-bold">1</span>
          Rechercher un membre
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8fa4b5]" size={18} />
          <input
            type="text" data-testid="member-search-input"
            placeholder="Nom, prénom, email ou téléphone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setSelectedMember(null); }}
            className="w-full pl-10 pr-4 py-3 border border-[#dfe5eb] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#3899ec]/30 focus:border-[#3899ec]"
          />
        </div>

        {loading && <p className="text-xs text-[#8fa4b5] mt-2">Recherche...</p>}

        {!selectedMember && members.length > 0 && (
          <div className="mt-3 border border-[#dfe5eb] rounded-lg max-h-60 overflow-y-auto" data-testid="member-results-list">
            {members.map((m) => (
              <button
                key={m.id} data-testid={`member-result-${m.id}`}
                onClick={() => { setSelectedMember(m); setSearch(`${m.first_name} ${m.last_name}`); setMembers([]); }}
                className="w-full text-left px-4 py-3 hover:bg-[#f4f7fa] border-b border-[#edf0f4] last:border-b-0 flex items-center gap-3 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#e8ecf0] flex items-center justify-center">
                  <User size={14} className="text-[#577083]" />
                </div>
                <div>
                  <span className="text-sm font-medium text-[#162d3d]">{m.last_name} {m.first_name}</span>
                  <span className="text-xs text-[#8fa4b5] ml-2">{m.email}</span>
                </div>
              </button>
            ))}
          </div>
        )}

        {selectedMember && (
          <div className="mt-3 p-4 bg-[#f0f7ff] rounded-lg border border-[#3899ec]/20" data-testid="selected-member-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3899ec] flex items-center justify-center text-white font-bold text-sm">
                {selectedMember.first_name?.[0]}{selectedMember.last_name?.[0]}
              </div>
              <div>
                <p className="text-sm font-semibold text-[#162d3d]">{selectedMember.first_name} {selectedMember.last_name}</p>
                <p className="text-xs text-[#577083]">
                  {selectedMember.email ? (
                    <span className="flex items-center gap-1"><Mail size={11} /> {selectedMember.email}</span>
                  ) : (
                    <span className="text-orange-500">Pas d'adresse email</span>
                  )}
                  {selectedMember.phone && <span className="ml-2">· {selectedMember.phone}</span>}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Step 2: Choose mutuelle */}
      <div className={`bg-white rounded-xl border border-[#dfe5eb] p-6 mb-6 shadow-sm transition-opacity ${selectedMember ? "opacity-100" : "opacity-50 pointer-events-none"}`} data-testid="mutuelle-select-section">
        <h2 className="text-sm font-semibold text-[#162d3d] mb-4 flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-[#3899ec] text-white flex items-center justify-center text-xs font-bold">2</span>
          Choisir la mutuelle
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {MUTUELLE_OPTIONS.map((mut) => (
            <button
              key={mut} data-testid={`mutuelle-option-${mut.replace(/ /g, "-")}`}
              onClick={() => setSelectedMutuelle(mut)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                selectedMutuelle === mut ? "border-[#3899ec] bg-[#f0f7ff] text-[#162d3d]" : "border-[#dfe5eb] hover:border-[#b6c1cd] text-[#577083]"
              }`}
            >
              <span className="text-sm font-medium">{mut}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Step 3: Actions */}
      <div className={`transition-opacity ${canAct ? "opacity-100" : "opacity-50 pointer-events-none"}`}>
        {/* Email status feedback */}
        {emailStatus && (
          <div className={`mb-4 p-4 rounded-lg border flex items-center gap-3 ${
            emailStatus.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-700"
          }`} data-testid="email-status">
            {emailStatus.type === "success" ? <Check size={18} /> : <Mail size={18} />}
            <span className="text-sm">{emailStatus.message}</span>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="generate-mutuelle-pdf-btn"
            onClick={handleGenerate}
            disabled={generating || !canAct}
            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-[#3899ec] hover:bg-[#2b7dca] disabled:bg-[#b6c1cd] text-white rounded-xl text-sm font-semibold transition-colors shadow-md"
          >
            {generating ? (
              <><Loader2 size={18} className="animate-spin" /> Génération...</>
            ) : (
              <><Download size={18} /> Télécharger le PDF</>
            )}
          </button>
          <button
            data-testid="send-mutuelle-email-btn"
            onClick={handleSendEmail}
            disabled={sending || !canAct || !selectedMember?.email}
            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 bg-[#22c55e] hover:bg-[#16a34a] disabled:bg-[#b6c1cd] text-white rounded-xl text-sm font-semibold transition-colors shadow-md"
          >
            {sending ? (
              <><Loader2 size={18} className="animate-spin" /> Envoi en cours...</>
            ) : (
              <><Mail size={18} /> Envoyer par email</>
            )}
          </button>
        </div>
        {selectedMember && !selectedMember.email && (
          <p className="text-xs text-orange-500 mt-2 text-center">
            L'envoi par email est désactivé : ce membre n'a pas d'adresse email renseignée.
          </p>
        )}
      </div>
    </div>
  );
};

export default MutuellePage;
